import { ApiUrl, Call, ParseGeneralResponse } from '@api/ApiUtils';
import { LineItemHistoriesResponse } from '@components/bid_request/details/ChangeLogDrawer/types';
import { ID } from 'types';

// AntD Table emits 'ascend' / 'descend'; the Rails Query service accepts
// either short ('asc'/'desc') or long ('ascend'/'descend') forms.
export type ChangeLogSortOrder = 'ascend' | 'descend';

interface ChangeLogWhodunnitsResponse {
  whodunnits: string[];
}

export const getChangeLogWhodunnits = (bidRequestId: ID): Promise<ChangeLogWhodunnitsResponse> =>
  Call(
    'get',
    ApiUrl(`/bid_requests/${bidRequestId}/line_item_histories/whodunnits`),
    {},
    ParseGeneralResponse,
    { responseDataKeys: { whodunnits: 'whodunnits' } }
  );

// Mirrors the Rails controller's nested `where[...]` permit list — adding a
// new filter is a one-liner here AND in `LineItemHistories::Query`. Keys are
// snake_case to keep that mirror literal.
export interface ChangeLogFilters {
  /** Column names; keeps only rows whose `object_changes` diff touched one of them. */
  changed_keys?: string[];
  /** Restricts to `LineItem` rows for these ids. */
  line_item_ids?: ID[];
  search?: string;
  whodunnits?: string[];
}

export interface FetchChangeLogHistoryParams {
  bidRequestId: ID;
  page: number;
  perPage: number;
  sort: string;
  sortOrder: ChangeLogSortOrder;
  where: ChangeLogFilters;
}

export const fetchChangeLogHistory = ({
  bidRequestId,
  page,
  perPage,
  sort,
  sortOrder,
  where
}: FetchChangeLogHistoryParams): Promise<LineItemHistoriesResponse> => {
  // Build the query string manually with encodeURIComponent (uses %20 for spaces).
  // URLSearchParams encodes spaces as `+`, which then gets escaped to %2B by
  // ApiUtils' safeQueryString — that mangles multi-word searches like "item 2".
  const parts = [
    `page_num=${page}`,
    `per_page=${perPage}`,
    `sort=${sort}`,
    `sort_order=${sortOrder}`
  ];
  if (where.search) parts.push(`where[search]=${encodeURIComponent(where.search)}`);
  where.whodunnits?.forEach(w => parts.push(`where[whodunnits][]=${encodeURIComponent(w)}`));
  where.line_item_ids?.forEach(id => parts.push(`where[line_item_ids][]=${encodeURIComponent(String(id))}`));
  where.changed_keys?.forEach(key => parts.push(`where[changed_keys][]=${encodeURIComponent(key)}`));

  return Call(
    'get',
    ApiUrl(`/bid_requests/${bidRequestId}/line_item_histories?${parts.join('&')}`),
    {},
    ParseGeneralResponse,
    { responseDataKeys: { pagy: 'pagy', records: 'records' } }
  );
};
